import React, { useState } from 'react';
import { targetAPI } from '../api';
import { InfoTooltip, ErrorState } from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';

// The Table Component
const TargetTable = ({ rows }) => {
  if (!rows || rows.length === 0) return <p>No clinical target data found.</p>;

  const checkModality = (tractability, modalityCode) => {
    if (!Array.isArray(tractability)) return false;
    return tractability.some(t => t.modality === modalityCode && t.value === true);
  };

  return (
    <div style={{ overflowX: 'auto', marginTop: '16px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px' }}>Gene</th>
            <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px' }}>Small Molecule</th>
            <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px' }}>Antibody</th>
            <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px' }}>PROTAC</th>
            <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', padding: '8px' }}>AD Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const sm = checkModality(row.tractability, 'SM');
            const ab = checkModality(row.tractability, 'AB');
            const pr = checkModality(row.tractability, 'PR');
            const adScore = row.alzheimers_association?.score;

            return (
              <tr key={idx}>
                <td style={{ borderBottom: '1px solid #eee', padding: '8px', fontWeight: 'bold' }}>{row.symbol}</td>
                <td style={{ borderBottom: '1px solid #eee', padding: '8px', color: sm ? '#28a745' : '#ccc' }}>{sm ? 'Yes' : 'No'}</td>
                <td style={{ borderBottom: '1px solid #eee', padding: '8px', color: ab ? '#28a745' : '#ccc' }}>{ab ? 'Yes' : 'No'}</td>
                <td style={{ borderBottom: '1px solid #eee', padding: '8px', color: pr ? '#28a745' : '#ccc' }}>{pr ? 'Yes' : 'No'}</td>
                <td style={{ borderBottom: '1px solid #eee', padding: '8px' }}>
                  {adScore !== undefined ? <span style={{ color: adScore > 0.5 ? '#d32f2f' : 'inherit' }}>{adScore.toFixed(4)}</span> : <span style={{ color: '#999' }}>No data</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// The Main Wrapper Component
export default function TargetAnnotations({ featureImportance }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [targetData, setTargetData] = useState(null);
  const [topCount, setTopCount] = useState(10);

  const handleFetchTargets = async () => {
    if (!featureImportance) return;
    setLoading(true);
    setError(null);

    try {
      let allFeatures = Array.isArray(featureImportance) 
        ? featureImportance.map(f => f.feature || f.name || Object.keys(f)[0]) 
        : [];

      const genesToSend = allFeatures
        .filter(g => g && !g.includes(' ') && !g.toLowerCase().startsWith('is_'))
        .slice(0, topCount);

      if (genesToSend.length === 0) throw new Error("No valid gene symbols found.");

      const response = await targetAPI.getTargetAnnotations(genesToSend);
      setTargetData(response.annotations || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch Open Targets annotations.');
    } finally {
      setLoading(false);
    }
  };

  if (!featureImportance) return null; // Hide if no features are loaded yet

  return (
    <div style={{ padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: '#fff', marginTop: '24px' }}>
      <InfoTooltip title="Target Annotations (Open Targets)" content="Check if your model's top features can be targeted by drugs." >
        <h2 style={{ marginTop: 0, marginBottom: '16px' }}>Target Annotations (Open Targets)</h2>
      </InfoTooltip>
      
      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
        <select value={topCount} onChange={(e) => setTopCount(Number(e.target.value))} style={{ padding: '4px 8px' }}>
          <option value={5}>Top 5</option>
          <option value={10}>Top 10</option>
          <option value={20}>Top 20</option>
        </select>

        <button 
          onClick={handleFetchTargets} disabled={loading}
          style={{ padding: '8px 16px', backgroundColor: loading ? '#ccc' : '#0056b3', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Checking...' : 'Check Drug Tractability'}
        </button>
      </div>

      {loading && <LoadingSpinner text="Querying Open Targets..." />}
      {error && <ErrorState message={error} onRetry={handleFetchTargets} />}
      {!loading && !error && targetData && <TargetTable rows={targetData} />}
    </div>
  );
}